import React, { useCallback, useEffect, useMemo } from 'react';
import { ReactFlowProvider, useReactFlow } from '@xyflow/react';
import { Loader2 } from 'lucide-react';
import { SubtleLink } from '@cotera/client/app/components/ui/link';
import { useQuery } from '@tanstack/react-query';
import { ResourceType } from '@cotera/api';
import { useWatchableValue } from '@cotera/client/v0/actions/framework';
import { useArtifactPanel } from '@cotera/client/v0/chat/artifact-panel-context';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import { Text } from '@cotera/client/app/components/ui';
import { Canvas } from '@cotera/client/v0/components/ai-elements/canvas';
import { WORKFLOW_EDGE_OPTIONS } from './workflow-edge-style';
import { WorkflowRenderCatalogProvider } from './workflow-render-catalog';
import { useWorkflowModel } from './workflow.model';
import { useNestedWorkflowTree } from './use-nested-workflow-tree';
import { useNestedWorkflowHierarchyEdits } from './use-nested-workflow-hierarchy-edits';
import { useNestedTreeCollapse } from './use-nested-tree-collapse';
import { nestedWorkflowTreeToFlow } from './nested-workflow-tree-to-flow';
import { NESTED_TREE_NODE_TYPES } from './nested-workflow-tree-nodes';
import {
  NestedTreeCapabilitiesProvider,
  type NestedTreeCapabilities,
  type OpenNestedWorkflow,
} from './nested-workflow-tree-context';
import { useWorkflowOptions } from '../trigger/hooks';
import { resourceItemPath } from '../resources/navigation';
import { readWorkflowResource } from './workflow-resource-read';
import {
  WORKFLOW_GRAPH_VIEW_TYPE,
  type WorkflowTreeViewPayload,
} from './workflow-artifact-views';
import type { NestedWorkflowTreeSummary } from './nested-workflow-tree';
import type { WorkflowGraphApiData } from './workflow-graph.types';

/** Zoomed out far enough that a wide tree still fits in the panel. */
const NESTED_TREE_FIT_VIEW_OPTIONS = { padding: 0.12, maxZoom: 1 } as const;

/**
 * Re-fits whenever the laid-out tree changes shape.
 *
 * Levels arrive one fetch at a time, so the graph grows to the right under the
 * viewport: fitting only on mount would frame the root and leave every workflow
 * below it off screen.
 */
const NestedTreeFitView: React.FC<{ layoutKey: string }> = ({ layoutKey }) => {
  const { fitView } = useReactFlow();

  useEffect(() => {
    if (layoutKey === '') {
      return;
    }
    const frame = requestAnimationFrame(() => {
      void fitView(NESTED_TREE_FIT_VIEW_OPTIONS);
    });
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [layoutKey, fitView]);

  return null;
};

/**
 * The one line of shape the header carries: how big this hierarchy is.
 *
 * No "loading…" segment — the spinner beside it already says so, and a word that
 * appears and disappears mid-sentence made the line reflow on every fetch. Says
 * "2 levels" rather than "2 levels deep": next to "4 workflows" the depth is
 * unambiguous, and this sits in a strip where every word competes.
 */
function summaryLine(summary: NestedWorkflowTreeSummary | null): string {
  if (summary === null) {
    return 'Resolving nested workflows…';
  }
  const workflows = summary.nestedCount + 1;
  const parts = [
    workflows === 1 ? '1 workflow' : `${workflows} workflows`,
    summary.maxDepth === 0
      ? 'no nesting'
      : `${summary.maxDepth} ${summary.maxDepth === 1 ? 'level' : 'levels'}`,
  ];
  if (summary.missingCount > 0) {
    parts.push(`${summary.missingCount} unavailable`);
  }
  return parts.join(' · ');
}

/**
 * The graph this tree is rooted at, and its name.
 *
 * Prefers the surrounding page's model, whose graph is the live draft — that is
 * what puts unsaved `run-workflow` nodes in the tree. Falls back to fetching by id
 * for the case where they differ: this view was pushed from a nested workflow's
 * own canvas, which the parent page hosts under its own model, so reading the
 * model there would draw the wrong workflow's tree.
 */
function useNestedTreeRoot(workflowId: string): {
  graph: WorkflowGraphApiData | null;
  name: string | null;
  /**
   * The workflow this tree was opened from, when that is not the tree's own
   * root — the page hosts it under its own model (see above).
   *
   * The one upstream fact this view can state. The tree only ever walks
   * downstream, following `targetWorkflowId` out of graphs it has already
   * fetched; going the other way would need an index of which workflows call a
   * given one, which the API does not have. So this names where you came from,
   * not every caller — and after a drill-down of several levels it is an
   * ancestor rather than the immediate parent, which is why the copy says "in"
   * rather than "parent".
   */
  ancestor: { id: string; name: string | null } | null;
} {
  const model = useWorkflowModel();
  const modelGraph = useWatchableValue(model.graphWatchable);
  const modelName = useWatchableValue(model.nameWatchable);
  const orgId = useWhoami((s) => s.org.id);
  const client = useTenantedClient();

  const isModelWorkflow = workflowId === model.workflowId;
  const { data } = useQuery({
    queryKey: queryKeys.resource.get({
      orgId,
      type: ResourceType.Workflow,
      id: workflowId,
    }),
    enabled: !isModelWorkflow,
    queryFn: () =>
      client.resource.get({ type: ResourceType.Workflow, id: workflowId }),
  });
  const fetched = readWorkflowResource(data);

  return isModelWorkflow
    ? { graph: modelGraph, name: modelName, ancestor: null }
    : {
        graph: fetched?.graph ?? null,
        name: fetched?.name ?? null,
        ancestor: { id: model.workflowId, name: modelName },
      };
}

/**
 * The whole nested workflow hierarchy as one graph, in place of the canvas.
 *
 * Pushed onto the artifact stack the same way a single nested workflow is, so it
 * gets the full width of the panel, its own breadcrumb, and the same back button
 * — this is a view of the workflow, not a panel floating over it.
 */
export const WorkflowNestedTreeStackContent: React.FC<{
  view: WorkflowTreeViewPayload;
}> = ({ view }) => {
  const model = useWorkflowModel();
  const artifactPanel = useArtifactPanel();
  const { graph, name, ancestor } = useNestedTreeRoot(view.workflowId);
  const orgSlug = useWhoami((state) => state.org.slug);

  const { tree, summary, isLoading } = useNestedWorkflowTree({
    workflowId: view.workflowId,
    name,
    graph,
  });

  const { isCollapsed, setCollapsed } = useNestedTreeCollapse();

  const flow = useMemo(
    () =>
      tree === null ? null : nestedWorkflowTreeToFlow(tree, { isCollapsed }),
    [tree, isCollapsed]
  );

  const layoutKey = useMemo(
    () =>
      flow === null
        ? ''
        : flow.nodes
            .map(
              (node) =>
                `${node.id}:${node.position.x.toFixed(
                  0
                )},${node.position.y.toFixed(0)}`
            )
            .join('|'),
    [flow]
  );

  // Replaces this view rather than stacking on top of it: the same entry the
  // run-workflow node editor pushes, so a child workflow always opens at one
  // breadcrumb rather than two.
  const openWorkflow = useCallback<OpenNestedWorkflow>(
    ({ workflowId, name: workflowName }) => {
      if (artifactPanel === null) {
        return;
      }
      artifactPanel.pushView(
        {
          id: `workflow-graph-${workflowId}`,
          title: workflowName ?? 'Workflow',
          payload: {
            type: WORKFLOW_GRAPH_VIEW_TYPE,
            workflowId: view.workflowId,
            targetWorkflowId: workflowId,
            ...(workflowName !== null && workflowName !== ''
              ? { targetWorkflowName: workflowName }
              : {}),
          },
        },
        { replaceTop: true }
      );
    },
    [artifactPanel, view.workflowId]
  );

  // Hierarchy editing: which workflow a step runs, and whether the step is there.
  // Everything else stays read-only — the group header's "open" button leads to
  // the real editor for that.
  const editor = useNestedWorkflowHierarchyEdits();
  const workflows = useWorkflowOptions();
  const capabilities = useMemo<NestedTreeCapabilities>(
    () => ({
      openWorkflow: artifactPanel === null ? null : openWorkflow,
      setCollapsed,
      editing: { editor, workflows },
    }),
    [artifactPanel, editor, openWorkflow, setCollapsed, workflows]
  );

  return (
    <NestedTreeCapabilitiesProvider value={capabilities}>
      <div className="flex h-full min-h-0 w-full flex-col overflow-hidden">
        {/* One row, and no heading in it. "Hierarchy" already names this view
            twice over — the shell breadcrumb and the artifact panel's own stack
            entry — so a third copy pushed the only line with information in it
            down into a subtitle. What is left is a status strip saying what this
            hierarchy contains.

            No pan/zoom hint either: `Canvas` renders React Flow's own zoom
            controls, and the workflow canvas this mirrors has never explained
            itself. */}
        <div className="flex shrink-0 items-center gap-1.5 border-b px-4 py-2">
          {isLoading ? (
            <Loader2 className="text-muted-foreground h-3 w-3 shrink-0 animate-spin" />
          ) : null}
          <Text.Caption className="text-muted-foreground min-w-0 truncate">
            {summaryLine(summary)}
          </Text.Caption>
          {/* Far right, and deliberately quiet: the shape of the hierarchy is
              what this strip is for, and this is a way *out* of it. Right-aligned
              also keeps it off the summary's sentence, which grows a segment when
              some children are unavailable. */}
          {ancestor !== null ? (
            <Text.Caption className="text-muted-foreground ml-auto flex min-w-0 items-center gap-1">
              <span className="shrink-0">Nested in</span>
              <SubtleLink
                to={resourceItemPath(
                  orgSlug,
                  ResourceType.Workflow,
                  ancestor.id
                )}
                className="truncate"
              >
                {ancestor.name ?? 'the workflow above'}
              </SubtleLink>
            </Text.Caption>
          ) : null}
        </div>

        <div className="relative min-h-0 flex-1">
          {flow === null ? (
            <div className="text-muted-foreground flex h-full items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <Text.Caption>Loading the workflow graph…</Text.Caption>
            </div>
          ) : (
            <WorkflowRenderCatalogProvider catalog={model.renderCatalog}>
              <ReactFlowProvider>
                <Canvas
                  nodes={flow.nodes}
                  edges={flow.edges}
                  nodeTypes={NESTED_TREE_NODE_TYPES}
                  defaultEdgeOptions={WORKFLOW_EDGE_OPTIONS}
                  className="h-full w-full"
                  proOptions={{ hideAttribution: true }}
                  nodesDraggable={false}
                  nodesConnectable={false}
                  edgesReconnectable={false}
                  elementsSelectable={false}
                  panOnDrag
                  panOnScroll={false}
                  zoomOnScroll
                  selectionOnDrag={false}
                  minZoom={0.1}
                  fitView={false}
                  fitViewOptions={NESTED_TREE_FIT_VIEW_OPTIONS}
                >
                  <NestedTreeFitView layoutKey={layoutKey} />
                </Canvas>
              </ReactFlowProvider>
            </WorkflowRenderCatalogProvider>
          )}
        </div>
      </div>
    </NestedTreeCapabilitiesProvider>
  );
};
